import * as React from 'react'
import { cn } from '../../../lib/cn'

export interface TraitOption {
  /** Wire name — the value stored in `required_traits`. */
  name: string
  /** Human-readable label shown on the chip. */
  label: string
}

export interface TraitPickerProps {
  /** Non-identifier fields from the tenant's active schema (`is_identifier === false`). */
  options: TraitOption[]
  /** Selected trait names (subset of options[].name). */
  value: string[]
  onChange: (next: string[]) => void
  /**
   * Highlight a specific trait chip when the publish-validator returns
   * FLOW_REF_UNKNOWN_TRAIT. Pass the offending trait name.
   */
  errorFor?: string
  disabled?: boolean
  className?: string
}

/**
 * TraitPicker — multi-select chip row for non-identifier schema traits.
 *
 * Used alongside IdentifierChannelPicker in the `identifier_lookup` block
 * config panel. Reads the tenant active schema fields where `is_identifier`
 * is false.
 *
 * Accessibility contract: same as IdentifierChannelPicker — `<button>` chips
 * with `aria-pressed`, `aria-invalid` for errored chips, screen-reader
 * description for error context.
 */
export const TraitPicker = React.forwardRef<HTMLDivElement, TraitPickerProps>(
  ({ options, value, onChange, errorFor, disabled = false, className }, ref) => {
    if (options.length === 0) {
      return (
        <div
          ref={ref}
          className={cn(
            'rounded-md border border-ia-border bg-ia-elevated px-3 py-2 text-[11px] text-ia-text-muted',
            className,
          )}
          role="status"
          aria-live="polite"
        >
          No non-identifier trait fields configured. Visit Identity &amp; Sign-in to add one.
        </div>
      )
    }

    const toggle = (name: string) => {
      if (disabled) return
      const next = value.includes(name) ? value.filter((v) => v !== name) : [...value, name]
      onChange(next)
    }

    return (
      <div ref={ref} className={cn('flex flex-wrap gap-1.5', className)}>
        {options.map((opt) => {
          const isSelected = value.includes(opt.name)
          const hasError = opt.name === errorFor
          const errorId = hasError ? `tp-error-${opt.name}` : undefined

          return (
            <React.Fragment key={opt.name}>
              <button
                type="button"
                aria-pressed={isSelected}
                aria-invalid={hasError ? true : undefined}
                aria-describedby={errorId}
                disabled={disabled}
                onClick={() => toggle(opt.name)}
                className={cn(
                  'rounded-pill border px-2.5 py-1 text-xs transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
                  'focus-visible:ring-ia-border-focus',
                  disabled && 'cursor-not-allowed opacity-50',
                  hasError
                    ? 'border-ia-error bg-ia-error/10 text-ia-error'
                    : isSelected
                      ? 'border-brand-500/40 bg-brand-500/15 text-foreground hover:bg-brand-500/25'
                      : 'border-ia-border bg-transparent text-muted-foreground hover:border-ia-border-strong hover:text-foreground',
                )}
              >
                {opt.label}
              </button>
              {hasError && (
                <span id={errorId} className="sr-only">
                  Unknown trait &ldquo;{opt.name}&rdquo; — not in the active identity schema.
                </span>
              )}
            </React.Fragment>
          )
        })}
      </div>
    )
  },
)

TraitPicker.displayName = 'TraitPicker'
